import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import LogoOne from "../../../assets/images/logo.png";
import EmailVerifyForm from './EmailVerifyForm';

// Email verification is state-aware — the backend returns three distinct outcomes on SUCCESS:
//
//   1. setupToken present  → admin already approved + password not yet created
//                           → navigate to /setup-password?token=<setupToken>
//
//   2. setupToken absent + SUCCESS → two sub-cases, both show a "verified" message:
//      a. Admin approval still pending (SUBMITTED) → show "awaiting admin approval" copy.
//      b. Admin approved + password already created → show generic "verified" copy.
//      In both cases we do NOT navigate to password setup.
//
//   3. TOKEN_EXPIRED → show resend form (EmailVerifyForm).
//   4. Other error  → show error message.
type VerifyState = 'verifying' | 'redirecting' | 'verified' | 'verified_pending_approval' | 'expired' | 'error';

const EmailVerifyPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [state, setState] = useState<VerifyState>('verifying');

  useEffect(() => {
    if (!token) {
      toast.error('Email verification link expired.');
      setState('expired');
      return;
    }

    axios
      .post(`${process.env.REACT_APP_API_GATEWAY_HOST}/api/user-management/v1/users/email/verify`, { token })
      .then((response) => {
        if (response.data.responseCode === 'SUCCESS') {
          const setupToken: string | null = response.data.setupToken ?? null;

          if (setupToken) {
            // Admin already approved and password not yet created — proceed to setup.
            setState('redirecting');
            toast.success('Email verified successfully.');
            navigate(`/setup-password?token=${encodeURIComponent(setupToken)}`);
          } else {
            // Either admin approval is still pending, or password already exists.
            // The backend message distinguishes the two cases; we use it to pick the
            // right display state.
            const msg: string = response.data.responseMsg ?? '';
            if (msg.toLowerCase().includes('pending admin approval')) {
              setState('verified_pending_approval');
            } else {
              setState('verified');
            }
            toast.success('Email verified successfully.');
          }
        } else {
          setState('error');
        }
      })
      .catch((error) => {
          const responseCode = error?.response?.data?.responseCode;
          if (responseCode === 'TOKEN_EXPIRED') {
            toast.error('Email verification link expired.');
            setState('expired');
          } else if (responseCode === 'OPERATION_NOT_ALLOWED') {
            // Email already verified — show success message
            toast.success(error?.response?.data?.responseMsg || 'Email already verified.');
            setState('verified');
          } else {
            toast.error('Something went wrong verifying your email. Please try again shortly.');
            setState('error');
          }
        console.error('Email verification error:', error);
      });
    // Runs once on mount — token doesn't change during this page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend handling is delegated to the EmailVerifyForm component.

  const renderBody = () => {
    switch (state) {
      case 'verifying':
      case 'redirecting':
        return (
          <div className='text-center fs--1'>
            <Spinner animation='border' size='sm' className='me-2' />
            {state === 'redirecting' ? 'Redirecting to password setup…' : 'Verifying your email…'}
          </div>
        );

      case 'verified_pending_approval':
        return (
          <div className='text-center fs--1'>
            <p>Your email has been verified successfully.</p>
            <p>Your account is currently pending admin approval. You will receive a confirmation email once your registration has been reviewed.</p>
            <a href={`${process.env.REACT_APP_CLIENT_APP_HOST}/app`} className='link'>
              Back to login
            </a>
          </div>
        );

      case 'verified':
        return (
          <div className='text-center fs--1'>
            <p>Your email has been verified successfully.</p>
            <a href={`${process.env.REACT_APP_CLIENT_APP_HOST}/app`} className='link'>
              Back to login
            </a>
          </div>
        );

      case 'expired':
        return <EmailVerifyForm hasLabel />;

      case 'error':
      default:
        return (
          <div className='text-center fs--1'>
            <p>Something went wrong verifying your email. Please try again shortly.</p>
          </div>
        );
    }
  };

  return (
    <div>
      <Container fluid>
        <Row>
          <Col
            style={{ position: 'relative', marginTop: '100px' }}
            className='p-0 col-4 form-login'
          >
            <div className='login-wrap'>
              <Card className='h-100 pt-4 pb-5 p-4'>
                <img src={LogoOne} className='logoWidth pb-5' />
                <Card.Header className='cardHeader textLowerCase'>
                  {t('resource_2:emailVerification')}
                </Card.Header>
                <Card.Body className='p-2 bodyHeightEmailVerify'>{renderBody()}</Card.Body>
                <div className='col-12 fs--1 copyWriteText'>
                  Copyright © EA Technologies 2025. All rights reserved
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EmailVerifyPage;

